import { trackError } from '~/services/logger/error.utils';
import { SessionAdapter } from '~/services/adapters/session.adapter';
import { IApiService } from '~/services/interfaces/IApiService';
import { JStorageService } from '~/services/j-storage.service';
import {
    IAppleFirebaseResult,
    ILoginRequestData,
    TSsoLoginRequestParams
} from '~/types/server.types';
import { getForterToken } from '~/utils/client-experience.utils';
import { getQueryParam } from '~/utils/url.utils';
import { clearUserFromStorage } from '~/common/user-storage.utils';
import { convertMtaCookieToHeader } from '~/utils/dom.utils';

export class SessionService extends SessionAdapter {
    jStorageService: JStorageService;
    constructor({
        apiService,
        jStorageService
    }: {
        apiService: IApiService;
        jStorageService: JStorageService;
    }) {
        super(apiService);

        this.jStorageService = jStorageService;
    }

    getLoginRequestTemplateData() {
        let templateData = {};

        try {
            const importPublicTemplateStorageValue = sessionStorage.getItem(
                'import_public_template'
            );
            const importPublicTemplateParsedValue =
                importPublicTemplateStorageValue
                    ? JSON.parse(importPublicTemplateStorageValue)
                    : null;
            templateData = {
                source_template_id: importPublicTemplateParsedValue?.templateId,
                template_redeem_code:
                    importPublicTemplateParsedValue?.templateRedeemCode
            };
        } catch (error) {
            trackError({ error });
        }

        return templateData;
    }

    getLoginRequestTrackingParams() {
        return getQueryParam(['extend_trial', 'trial_type', 'utm_campaign']);
    }

    async loginReactFirst({
        email,
        password,
        source,
        sourceData,
        trust_device,
        g_recaptcha_response_v3,
        fingerprint
    }: Omit<
        ILoginRequestData,
        'f_token' | 'company_id' | 'g-recaptcha-response-v3'
    > & {
        g_recaptcha_response_v3?: string;
    }) {
        // Pre login
        clearUserFromStorage(this.jStorageService);

        // Prepare data for login method

        // TODO: https://honeybook.atlassian.net/browse/NG2REACT-36
        // I'm not sure we need this here, since we only need it clientPortalConfiguration in the CX session service
        const company_id = window.clientPortalConfiguration?.company_id;
        const f_token = getForterToken();

        const data = {
            password,
            trust_device: trust_device ?? true,
            source,
            sourceData,
            company_id,
            f_token,
            email,
            'g-recaptcha-response-v3': g_recaptcha_response_v3 ?? null,
            fingerprint,
            ...this.getLoginRequestTrackingParams(),
            ...this.getLoginRequestTemplateData()
        };

        const headers = convertMtaCookieToHeader();

        return super._loginReactFirst({ data, headers });
    }

    async loginWithSso(params: TSsoLoginRequestParams) {
        const data = {
            f_token: getForterToken(),
            ...this.getLoginRequestTrackingParams(),
            ...this.getLoginRequestTemplateData(),
            ...params
        };

        const headers = convertMtaCookieToHeader();

        return super._loginWithSso({ data, headers });
    }

    loginWithApple({
        appleLoginResult,
        fingerprint
    }: {
        appleLoginResult: IAppleFirebaseResult;
        fingerprint: string;
    }) {
        const credential = appleLoginResult.credential;
        if (!credential) {
            throw new Error('No apple login credential found');
        }

        const userProfile = appleLoginResult.additionalUserInfo.profile;
        const appleId = userProfile.sub;

        const data = {
            sso_type: 'apple' as const,
            id_token: credential.idToken,
            apple_id: appleId,
            auth_token: credential.accessToken,
            fingerprint
        };

        return this.loginWithSso(data);
    }

    loginWithGoogle({
        authToken,
        fingerprint
    }: {
        authToken: string;
        fingerprint: string;
    }) {
        const data = {
            sso_type: 'google' as const,
            auth_token: authToken,
            fingerprint
        };

        return this.loginWithSso(data);
    }

    async loginWithPasskeys({
        authToken,
        fingerprint,
        email
    }: {
        authToken: string;
        fingerprint: string;
        email?: string;
    }) {
        const data = {
            sso_type: 'passkeys' as const,
            auth_token: authToken,
            fingerprint,
            email
        };

        return this.loginWithSso(data);
    }
}
